/* Step-by-step government renewal guide for one document kind (contracts/gov-renewals.json).
 * Stable props used by other page groups: { kind, compact?, audience? }.
 * Also exports RenewalGuideDrawer (side drawer) and guideKindFor(docKind). */
import { useId, useState, type ReactNode } from "react";
import { Alert, Chip, Icon, Modal, Steps } from "../../../ui";
import { useI18n } from "../../../i18n";
import { fmtN, gDate } from "../../../lib/format";
import { guideFor, guideKindFor, keyLabel, KIND_AR, PLATFORM_AR, RENEWALS, shortUrl, stripNum, type Json, type RenewalGuideData } from "./renewals";
import "../core.css";

export { guideKindFor } from "./renewals";

type Audience = "employer" | "employee";

/* ---------- generic fee / penalty renderer ---------- */
function JsonValue({ v }: { v: Json }) {
  const { L, isAr } = useI18n();
  if (v === null) return <>—</>;
  if (typeof v === "boolean") return <>{v ? L("Yes", "نعم") : L("No", "لا")}</>;
  if (typeof v === "number") return <span className="num">{fmtN(v, Number.isInteger(v) ? 0 : 2)}</span>;
  if (typeof v === "string") return <>{v}</>;
  if (Array.isArray(v)) {
    if (v.every((x) => x === null || typeof x !== "object")) {
      if (v.every((x) => typeof x === "number")) return <span className="num">{v.join(" · ")}</span>;
      return <ul className="rg-ul">{v.map((x, i) => <li key={i}><JsonValue v={x} /></li>)}</ul>;
    }
    return (
      <ul className="rg-ul">
        {v.map((x, i) => (
          <li key={i}>
            {x && typeof x === "object" && !Array.isArray(x)
              ? Object.entries(x).map(([k, y], j) => <span key={k}>{j > 0 && " · "}{keyLabel(k, isAr)}: <JsonValue v={y} /></span>)
              : <JsonValue v={x} />}
          </li>
        ))}
      </ul>
    );
  }
  return <Facts obj={v} />;
}

function Facts({ obj }: { obj: { [k: string]: Json } }) {
  const { isAr } = useI18n();
  const entries = Object.entries(obj).filter(([k]) => k !== "verify");
  if (!entries.length) return null;
  return (
    <dl className="kv rg-kv">
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: "contents" }}>
          <dt>{keyLabel(k, isAr)}</dt>
          <dd><JsonValue v={v} /></dd>
        </div>
      ))}
    </dl>
  );
}

function Section({ title, icon, children, testId }: { title: ReactNode; icon: string; children: ReactNode; testId?: string }) {
  return (
    <section className="rg-sec" data-testid={testId}>
      <h4 className="rg-h"><Icon name={icon} className="s" />{title}</h4>
      {children}
    </section>
  );
}

function ExtLink({ href, children, className, testId }: { href: string; children: ReactNode; className?: string; testId?: string }) {
  return <a href={href} target="_blank" rel="noopener noreferrer" className={className} data-testid={testId}>{children}</a>;
}

/* ---------- full guide body ---------- */
function GuideBody({ g, audience }: { g: RenewalGuideData; audience?: Audience }) {
  const { L, isAr, lang } = useI18n();
  const [done, setDone] = useState<boolean[]>(() => g.prerequisites.map(() => false));
  const baseId = useId();
  const steps = (isAr && g.stepsAr?.length ? g.stepsAr : g.steps).map(stripNum);
  const tips = isAr && g.tipsAr?.length ? g.tipsAr : g.tips;
  const doneCount = done.filter(Boolean).length;
  const enOnly = isAr ? <span className="xs muted"> · {L("", "النص المصدر بالإنجليزية")}</span> : null;

  return (
    <div className="rg-body">
      {audience === "employee" && g.who === "employer" && (
        <Alert tone="info" tint title={L("Your employer handles this renewal", "صاحب العمل هو من يجدد هذه الوثيقة")} desc={L("HR submits it on the government platform. Keep your passport and insurance details up to date and respond quickly if HR asks for documents.", "يقدّم قسم الموارد البشرية الطلب على المنصة الحكومية. حافظ على تحديث بيانات جوازك وتأمينك واستجب بسرعة لأي طلب مستندات.")} testId="rg-audience-note" />
      )}
      {audience === "employer" && g.who === "employee" && (
        <Alert tone="info" tint title={L("The employee renews this document", "الموظف هو من يجدد هذه الوثيقة")} desc={L("Track the expiry here, remind the employee in time and upload the renewed copy to their file.", "تابع تاريخ الانتهاء هنا، وذكّر الموظف في الوقت المناسب، وارفع النسخة المجددة إلى ملفه.")} testId="rg-audience-note" />
      )}

      {g.leadTimeNote && <p className="sm muted rg-note">{g.leadTimeNote}{enOnly}</p>}

      <Section title={<>{L("Before you start", "قبل البدء")} <span className="xs muted num">{doneCount}/{g.prerequisites.length}</span></>} icon="check" testId="rg-prereqs">
        <ul className="rg-check">
          {g.prerequisites.map((p, i) => (
            <li key={i}>
              <input type="checkbox" id={baseId + "-p" + i} checked={done[i]} onChange={(e) => setDone((d) => d.map((x, j) => (j === i ? e.target.checked : x)))} />
              <label htmlFor={baseId + "-p" + i}>{p}</label>
            </li>
          ))}
        </ul>
        {enOnly}
      </Section>

      <Section title={L("Steps", "الخطوات")} icon="layers" testId="rg-steps">
        <Steps steps={steps} />
      </Section>

      <div className="g g-2 rg-money">
        <Section title={L("Fees", "الرسوم")} icon="banknote" testId="rg-fees">
          <Facts obj={g.fees} />
        </Section>
        <Section title={L("Penalties", "الغرامات")} icon="alert" testId="rg-penalties">
          <Facts obj={g.penalties} />
        </Section>
      </div>

      {tips.length > 0 && (
        <Section title={L("Tips", "نصائح")} icon="sparkle" testId="rg-tips">
          <ul className="rg-ul">{tips.map((t, i) => <li key={i}>{t}</li>)}</ul>
        </Section>
      )}

      <Section title={L("Sources", "المصادر")} icon="file" testId="rg-sources">
        <ul className="rg-ul rg-src">{g.sources.map((s) => <li key={s}><ExtLink href={s}><span dir="ltr">{shortUrl(s)}</span></ExtLink></li>)}</ul>
      </Section>

      {g.verify && (
        <Alert tone="warn" tint title={L("Verify before you rely on it", "تحقق قبل الاعتماد عليه")} desc={<>{L("Some details in this guide could not be confirmed on an official page. Check the official portal before paying or submitting.", "تعذّر تأكيد بعض تفاصيل هذا الدليل من صفحة رسمية. راجع البوابة الرسمية قبل السداد أو التقديم.")}{g.verifyNotes ? <><br /><span className="xs">{g.verifyNotes}</span></> : null}</>} testId="rg-verify" />
      )}
      <p className="xs muted rg-foot" data-testid="rg-verified">
        <Icon name="clock" className="s" /> {L("Last verified", "آخر تحقق")}: <span className="num">{gDate(RENEWALS.researchedOn, lang)}</span> · {L(RENEWALS.disclaimer, "جُمعت هذه المعلومات من البوابات الرسمية حيثما أمكن ومن مصادر ثانوية موثوقة. تتغير الرسوم والأنظمة الحكومية كثيرًا، لذا تحقق من البنود المعلّمة بـ«تحتاج تحققًا» على البوابة الرسمية قبل الاعتماد عليها.")}
      </p>
    </div>
  );
}

function Header({ g }: { g: RenewalGuideData }) {
  const { L, isAr } = useI18n();
  return (
    <div className="rg-head">
      <div className="rg-meta">
        <div className="row" style={{ gap: 6 }}>
          <Chip tone={g.who === "employer" ? "pri" : "info"} nodot testId="rg-who">{g.who === "employer" ? L("Employer task", "مهمة صاحب العمل") : L("Employee task", "مهمة الموظف")}</Chip>
          <Chip tone={g.leadTimeDays >= 60 ? "warn" : "info"} testId="rg-lead">
            {g.leadTimeDays > 0
              ? L("Start " + g.leadTimeDays + " days before expiry", "ابدأ قبل الانتهاء بـ " + g.leadTimeDays + " يومًا")
              : L("Do it on day one (no expiry window)", "يُنجز من اليوم الأول (بلا مهلة انتهاء)")}
          </Chip>
          {g.verify && <Chip tone="warn" nodot>{L("Verify", "تحتاج تحققًا")}</Chip>}
        </div>
        <p className="sm rg-platform"><b>{L("Platform", "المنصة")}:</b> {isAr ? PLATFORM_AR[g.kind] ?? g.platform : g.platform}</p>
      </div>
      <div className="row rg-links">
        <ExtLink href={g.url} className="btn pri sm" testId="rg-official">
          <Icon name="external" /><span>{L("Open official portal", "فتح البوابة الرسمية")}</span>
        </ExtLink>
        {(g.altUrls || []).map((u) => <ExtLink key={u} href={u} className="btn ghost sm"><span dir="ltr">{shortUrl(u)}</span></ExtLink>)}
      </div>
    </div>
  );
}

export function kindLabel(kind: string, isAr: boolean): string {
  return isAr ? KIND_AR[kind] ?? kind : kind;
}

export default function RenewalGuide({ kind, compact, audience }: { kind: string; compact?: boolean; audience?: Audience }) {
  const { L, isAr } = useI18n();
  const g = guideFor(kind);
  if (!g) {
    return (
      <div data-testid="renewal-guide" data-kind="">
        <Alert tone="info" title={L("No renewal guide for this document yet", "لا يوجد دليل تجديد لهذه الوثيقة بعد")} desc={L("Renew it on the issuing authority's portal and record the new expiry date here.", "جدّدها عبر بوابة الجهة المصدرة وسجّل تاريخ الانتهاء الجديد هنا.") + (kind ? " (" + kind + ")" : "")} />
      </div>
    );
  }
  const title = kindLabel(g.kind, isAr);
  if (compact) {
    return (
      <details className="rg rg-compact" data-testid="renewal-guide" data-kind={g.kind}>
        <summary className="rg-sum">
          <span className="kpi-ico"><Icon name="id-card" /></span>
          <span className="grow">
            <b>{L("How to renew", "طريقة التجديد")} · {title}</b>
            <span className="xs muted rg-sum-sub">
              {isAr ? PLATFORM_AR[g.kind] ?? g.platform : g.platform} · {g.leadTimeDays > 0 ? L("start " + g.leadTimeDays + " days before expiry", "ابدأ قبل الانتهاء بـ " + g.leadTimeDays + " يومًا") : L("on day one", "من اليوم الأول")}
            </span>
          </span>
          <Icon name="chevron-down" className="s rg-chev" />
        </summary>
        <div className="rg-inner">
          <Header g={g} />
          <GuideBody g={g} audience={audience} />
        </div>
      </details>
    );
  }
  return (
    <div className="rg" data-testid="renewal-guide" data-kind={g.kind}>
      <Header g={g} />
      <GuideBody g={g} audience={audience} />
    </div>
  );
}

/** Side drawer wrapper: "How to renew · Iqama". */
export function RenewalGuideDrawer({ kind, onClose, audience = "employer" }: { kind: string; onClose: () => void; audience?: Audience }) {
  const { L, isAr } = useI18n();
  const gk = guideKindFor(kind);
  return (
    <Modal className="drawer" title={L("How to renew", "طريقة التجديد") + " · " + (gk ? kindLabel(gk, isAr) : kind)} onClose={onClose} testId="renewal-drawer">
      <RenewalGuide kind={kind} audience={audience} />
    </Modal>
  );
}
